import React from "react";
import { Flex, Dropdown } from "antd";
import type { MenuProps } from "antd";
import { Container } from "../../atoms/container/Container";
import { Text } from "../../atoms/text/Text";
import { Icon } from "../../icon/Icon";
import { maskNumber } from "../../../utils/helper";
import { useAgentManualCall } from "../../../hooks/useAgentManualCall/UseAgentManualCall";
import { useDoctorPatientCall } from "../../../hooks/useDoctorPatientCall/UseDoctorPatientCall";
import { FaChevronDown } from "react-icons/fa";
import { FiPhone } from "react-icons/fi";
import style from "./doctorCard.module.scss";

interface EmergencyContactItem {
  fullName?: string;
  name?: string;
  mobile?: any;
  relationshipToBeneficiary?: string;
}

interface DoctorCardProps {
  doctorName: string;
  specialty?: string;
  number: string;
  startTime: string;
  endTime: string;
  patientNumber: string;
  pocNumber?: string;
  emergencyContacts?: EmergencyContactItem[];
}

const getMobileString = (mob: any): string => {
  if (!mob) return "";
  if (typeof mob === "string") return mob;
  if (typeof mob === "number") return String(mob);
  if (typeof mob === "object" && mob.number) return String(mob.number);
  return "";
};

export const DoctorCard: React.FC<DoctorCardProps> = ({
  doctorName,
  specialty,
  number,
  startTime,
  endTime,
  patientNumber,
  pocNumber,
  emergencyContacts = [],
}) => {
  const { handleConnectCall } = useAgentManualCall();
  const { handleConnectCallBtwDP } = useDoctorPatientCall();

  const emMenuItems = (emergencyContacts || [])
    .map((item, index) => {
      const mob = getMobileString(item?.mobile);
      if (!mob) return null;
      const contactLabel = item.fullName || item.name || (item.relationshipToBeneficiary ? `EM (${item.relationshipToBeneficiary})` : `EM ${index + 1}`);
      return {
        key: `doc_em_${index}_${mob}`,
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 2px" }}>
            <FiPhone size={13} color="#00A7B5" />
            <span>
              DOC &rarr; {contactLabel} ({maskNumber(mob)})
            </span>
          </div>
        ),
        onClick: () => handleConnectCallBtwDP(mob, number),
      };
    })
    .filter(Boolean) as MenuProps["items"];

  const menuItems: MenuProps["items"] = [
    {
      key: "doc",
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 2px" }}>
          <FiPhone size={13} color="#00A7B5" />
          <span>Doctor ({maskNumber(number)})</span>
        </div>
      ),
      onClick: () => handleConnectCall(number),
    },
    ...(patientNumber
      ? [
          {
            key: "doc_pat",
            label: (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 2px" }}>
                <FiPhone size={13} color="#00A7B5" />
                <span>DOC &rarr; PAT ({maskNumber(patientNumber)})</span>
              </div>
            ),
            onClick: () => handleConnectCallBtwDP(patientNumber, number),
          },
        ]
      : []),
    ...(pocNumber
      ? [
          {
            key: "doc_poc",
            label: (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "4px 2px" }}>
                <FiPhone size={13} color="#00A7B5" />
                <span>DOC &rarr; POC ({maskNumber(pocNumber)})</span>
              </div>
            ),
            onClick: () => handleConnectCallBtwDP(pocNumber, number),
          },
        ]
      : []),
    ...(emMenuItems || []),
  ];

  return (
    <Container className={style.card_wrapper}>
      <Container className={style.flex}>
        <Container>
          <Icon alt="check" src="/images/doctorPic.png" />
        </Container>
        <Container className={style.doctor_det}>
          <Text text={doctorName} className={style.name} />
          <Flex className={style.card_divider}>
            {specialty && <Text text={specialty} className={style.specialty} />}
            <Flex className={style.timing_divider}>
              <Text text={"Timing"} className={style.timing} />
              <Text text={`${startTime} - ${endTime}`} className={style.timing} />
            </Flex>
          </Flex>
          <div>
            <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
              <button className={style.callDropdownBtn} type="button">
                <div className={style.callBtnIconWrapper}>
                  <Icon
                    className={style.callBtnImg}
                    src={"/images/phone.png"}
                    alt="phoneIcon"
                  />
                </div>
                <span className={style.callBtnText}>Call Options</span>
                <FaChevronDown className={style.chevronIcon} />
              </button>
            </Dropdown>
          </div>
        </Container>
      </Container>
    </Container>
  );
};

